/**
 * Which workspace this browser tab is in.
 *
 * Resolved ONCE from the URL at module load, before React renders, and treated
 * as immutable for the life of the tab. That is deliberate: a tenant is not
 * application state that can change under you — changing workspace is a full
 * navigation, which reloads this module with a different answer.
 *
 * It also means two tenants can be open in two tabs without either affecting the
 * other, which a single "active tenant" stored in the session (or in Zustand)
 * could not offer.
 *
 * The slug is only ever a HINT. The API resolves it to a real tenant and then
 * validates the caller's membership and every resource's tenant foreign key, so
 * editing the URL changes which login page renders and nothing else.
 */

import {
  FIRST_PARTY_TENANT_SLUG,
  tenantSlugFromHostname,
  type LoginSurface,
} from '@cwy/config/contracts';

export interface TenantContext {
  /** The school's slug, or null at the root domain (the first-party tenant). */
  slug: string | null;
  /**
   * The router basename.
   *
   * This is the trick that keeps the whole app working under a school URL: with
   * a basename of `/school/dps-rkp`, every existing route ('/today',
   * '/practice', '/teacher') resolves unchanged, and every `<Link to="/today">`
   * renders as `/school/dps-rkp/today`. No route table had to be duplicated for
   * school tenants, and no component had to learn about workspaces.
   */
  basename: string;
  /** Which login surface the URL selects. Presentation only — never authority. */
  surface: LoginSurface;
}

/** `/school/<slug>` at the start of a path, with the slug captured. */
const SCHOOL_PATH = /^\/school\/([a-z0-9][a-z0-9-]{0,61}[a-z0-9])(?=\/|$)/;

/**
 * The login surface a path selects.
 *
 * Defined here, once, because three things have to agree about it: which login
 * box renders, where a signed-out visitor is sent, and which console a signed-in
 * person is allowed into. When the surface was inferred separately in each of
 * those places they drifted, and `/admin` ended up bouncing to the student login.
 *
 * Matched on a whole segment, so `/administrators` is not the admin surface.
 */
export function surfaceForPath(path: string): LoginSurface {
  if (path === '/admin' || path.startsWith('/admin/')) return 'ADMIN';
  if (path === '/teacher' || path.startsWith('/teacher/')) return 'TEACHER';
  return 'STUDENT';
}

/**
 * Where a signed-out visitor asking for each console surface is sent.
 *
 * TWO entrances, split on the one distinction that changes the form: `/login` for
 * students, `/staff` for everyone else. Neither `/teacher` nor `/admin` is a
 * login url — both are consoles only, and asking for either signed out redirects
 * here and is remembered.
 *
 * There is no third entrance for administrators because an admin is staff too,
 * and the form could not act on the difference anyway: it asks for an email and
 * a password, the same two things in either case. A separate `/admin` box only
 * ever split the same credential across two addresses, and made a school admin
 * who typed `/staff` look like they were at the wrong door.
 */
export const LOGIN_PATH: Record<LoginSurface, string> = {
  STUDENT: '/login',
  TEACHER: '/staff',
  ADMIN: '/staff',
};

/**
 * Which sign-in box a login URL renders.
 *
 * Deliberately NOT the same type as the console surface above, because the
 * mapping is not one to one: `/staff` is a single box serving teachers,
 * administrators and platform operators, who land in two different consoles.
 * Keeping the two ideas apart is what lets a person's console be decided by the
 * membership the API reports rather than by the URL they signed in through.
 */
export type LoginBox = 'STUDENT' | 'STAFF';

/**
 * The platform's root domain, from the build.
 *
 * Configured rather than inferred: a hostname cannot tell you where the
 * platform's domain ends and a tenant label begins. It must name the same root
 * domain the API derives from WEB_ORIGIN — the slug derived here is sent as a
 * hint header, and the API rejects a request whose host and header disagree.
 * Unset disables subdomain routing in the client, which is the safe default
 * while the `/school/:slug` path form is what ships.
 *
 * Vite inlines this at BUILD time, so it has to be a build variable, not a
 * runtime one.
 */
const PRIMARY_HOST = import.meta.env.VITE_PRIMARY_HOST?.trim().toLowerCase() ?? '';

/** The tenant slug this hostname implies. Parsing shared with the API. */
function slugFromHostname(hostname: string): string | null {
  return tenantSlugFromHostname(hostname, PRIMARY_HOST);
}

function resolve(): TenantContext {
  const { pathname, hostname } = window.location;

  const pathMatch = SCHOOL_PATH.exec(pathname);
  const pathSlug = pathMatch?.[1] ?? null;
  const hostSlug = slugFromHostname(hostname);

  // The surface is read from what follows the tenant prefix, so `/teacher` and
  // `/school/x/teacher` are the same surface in different workspaces.
  const rest = pathSlug ? pathname.slice(pathMatch![0].length) : pathname;
  const surface = surfaceForPath(rest);

  // A path slug wins over a host slug only because the path form is the one
  // shipping now. The API rejects a request whose host and header disagree, so a
  // genuine conflict surfaces as an error rather than a silent choice.
  const slug = pathSlug ?? hostSlug;

  return {
    slug,
    // Only the PATH form needs a basename. A subdomain serves the app at the
    // root of its own host, so its routes are already unprefixed.
    basename: pathSlug ? `/school/${pathSlug}` : '/',
    surface,
  };
}

/**
 * The tenant for this tab. Computed once, at import.
 *
 * Exported as a constant rather than a hook so that non-React code — the axios
 * interceptor in particular — can read it without a provider.
 */
export const tenant: TenantContext = resolve();

/** True at the root domain, where the first-party B2C tenant lives. */
export const isFirstParty = tenant.slug === null || tenant.slug === FIRST_PARTY_TENANT_SLUG;

/**
 * Build a path in the CURRENT workspace.
 *
 * Rarely needed: react-router's basename already prefixes every `<Link>`. Use
 * this only for a hard `window.location` navigation, which bypasses the router
 * entirely and would otherwise drop the workspace.
 */
export function tenantPath(path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return tenant.basename === '/' ? clean : `${tenant.basename}${clean}`;
}

/**
 * The surface the browser is on RIGHT NOW.
 *
 * `tenant.surface` is frozen at page load, which is correct for choosing the
 * login form but wrong for code that runs after client-side navigation — signing
 * out of a staff console has to return to `/staff`, not to whichever surface the
 * tab happened to open on.
 */
export function currentSurface(): LoginSurface {
  const path = window.location.pathname;
  const rest = tenant.basename === '/' ? path : path.slice(tenant.basename.length);
  return surfaceForPath(rest);
}

/** `/admin/schools/<slug>` at the start of a path — the platform console managing one school. */
const SUPPORT_PATH = /^\/admin\/schools\/([a-z0-9][a-z0-9-]{0,61}[a-z0-9])(?=\/|$)/;

/**
 * The school the platform console is managing as support, or null.
 *
 * Read from the CURRENT path, not frozen at load like `tenant`: the console
 * moves between schools by client-side navigation. Only meaningful at the root
 * domain — inside a school's own URL the tab already has a workspace, and this
 * must never override it.
 */
export function supportSchoolSlug(): string | null {
  if (tenant.slug) return null;
  return SUPPORT_PATH.exec(window.location.pathname)?.[1] ?? null;
}

/** Build a path in a DIFFERENT workspace, for the workspace switcher. */
export function pathInTenant(slug: string | null, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return slug && slug !== FIRST_PARTY_TENANT_SLUG ? `/school/${slug}${clean}` : clean;
}
